import type { Project } from "./useProjects";
import React from "react";
import { comparatorBy } from "diagnostics-lib/comparator";
import { Duration } from "luxon";
import type { Source } from "./useSources";
import { useProjectPeriod } from "../components/Project/useProjectPeriod";
import useSelectedQuantities from "../components/Project/useSelectedQuantities";
import useQuery, { useRevalidate } from "./useQuery";
import t from "../i18n";
import { locale } from "../i18n/locale";
import type { Analysis } from "./Analysis";
import { useChannel } from "./ws/useChannel";
import useDebouncedCallback from "@ui/lib/hooks/useDebouncedCallback";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";

const MAX_SNAPSHOT_DISPLAY_COUNT = 500;
const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = DAY_MS * 7;
const MONTH_MS = WEEK_MS * 4;
const YEAR_MS = MONTH_MS * 12;

export function samplingParameters(
  from: Date | null,
  to: Date | null,
  firstDate: Date | null,
  lastDate: Date | null,
  snapshotCount: number,
  maxSnapshotDisplayCount: number,
) {
  if (firstDate === null || lastDate === null) return null;
  from = from ?? firstDate;
  to = to ?? lastDate;
  const askedRange =
    Math.min(to.getTime(), lastDate.getTime()) - Math.max(from.getTime(), firstDate.getTime());
  const maxRange = lastDate.getTime() - firstDate.getTime();
  const density = maxRange ? snapshotCount / maxRange : 0;
  const estimatedCount = askedRange * density;
  const sampling = estimatedCount > maxSnapshotDisplayCount;
  let intervalSeconds = 0;
  let intervalCount: number | null = null;
  if (sampling) {
    const diff = to.getTime() - from.getTime();
    const interval = diff / maxSnapshotDisplayCount;
    const intervalSizeMs = [
      SECOND_MS,
      5 * SECOND_MS,
      20 * SECOND_MS,
      MINUTE_MS,
      5 * MINUTE_MS,
      20 * MINUTE_MS,
      HOUR_MS,
      6 * HOUR_MS,
      DAY_MS,
      WEEK_MS,
      MONTH_MS,
      6 * MONTH_MS,
      YEAR_MS,
    ].find(is => is >= interval);
    if (intervalSizeMs === undefined) throw new Error("Interval not found");

    from = new Date(Math.floor(from.getTime() / intervalSizeMs) * intervalSizeMs);
    to = new Date(Math.ceil(to.getTime() / intervalSizeMs) * intervalSizeMs);
    intervalSeconds = intervalSizeMs / 1000;
    intervalCount = (to.getTime() - from.getTime()) / intervalSizeMs;
  }

  return {
    from,
    to,
    intervalSeconds,
    estimatedCount,
    intervalCount,
  };
}

export const getWindowSampling = ({ period, source }: { source: Source; period: Period }) =>
  samplingParameters(
    new Date(period.from),
    new Date(period.to),
    source.minSnapshotDate ? new Date(source.minSnapshotDate) : null,
    source.maxSnapshotDate ? new Date(source.maxSnapshotDate) : null,
    source.snapshotCount,
    MAX_SNAPSHOT_DISPLAY_COUNT,
  );

const getWindowSamplingQuery = ({
  quantityIds,
  period,
  source,
}: {
  quantityIds: string[];
  source: Source;
  period: Period;
}) => {
  const windowSampling = getWindowSampling({ period, source });

  return {
    from: windowSampling!.from,
    to: windowSampling!.to,
    intervalSeconds: windowSampling!.intervalSeconds,
    sourceId: source.id,
    quantityIds: quantityIds.join(","),
    mustHaveLastAnalysis: false,
  };
};
const arrayUniqueByKey = <T>(array: T[], key: keyof T) => [
  ...new Map(array.map(item => [item[key], item])).values(),
];

const units = ["years", "months", "days", "hours", "minutes", "seconds", "milliseconds"] as const;
type Unit = (typeof units)[number];

function toHuman(dur: Duration, smallestUnit: Unit = "seconds"): string {
  const smallestIdx = units.indexOf(smallestUnit);
  const entries = Object.entries(
    dur
      .shiftTo(...units)
      .normalize()
      .toObject(),
  ).filter(([_unit, amount], idx) => amount > 0 && idx <= smallestIdx);
  const dur2 = Duration.fromObject(
    entries.length === 0 ? { [smallestUnit]: 0 } : Object.fromEntries(entries),
    { locale },
  );
  return dur2.toHuman();
}

function useAllAnalyses(
  project: Project,
  source: Source,
  doIt: boolean,
  activeAnalyses: Analysis[],
): {
  result: Analysis[];
  description: string | null;
} {
  const revalidate = useRevalidate();
  const projectId = project.id;
  const callback = useDebouncedCallback(
    () => {
      void revalidate("/projects/:projectId(uuid)/all-analyses", { projectId });
    },
    [revalidate, projectId],
    2_000,
    {
      leading: true,
      maxWait: 2_000,
      trailing: true,
    },
  );

  useChannel({ key: "all_analyses", projectId }, callback, true);

  const [period] = useProjectPeriod({ source, project, boundaries: "analyses" });
  const [selectedQuantities] = useSelectedQuantities(projectId);

  const overviewSampling = React.useMemo(
    () =>
      samplingParameters(
        null,
        null,
        source.minSnapshotDate ? new Date(source.minSnapshotDate) : null,
        source.maxSnapshotDate ? new Date(source.maxSnapshotDate) : null,
        source.snapshotCount,
        MAX_SNAPSHOT_DISPLAY_COUNT,
      ),
    [source],
  );

  const windowSampling = React.useMemo(
    () => getWindowSampling({ period, source }),
    [period, source],
  );

  const quantityIds = React.useMemo(
    () => selectedQuantities.map(q => q.quantity.id),
    [selectedQuantities],
  );

  const placeholderData = React.useMemo(
    () =>
      ({
        status: 200,
        body: { result: activeAnalyses, unsampledCount: activeAnalyses.length },
        headers: undefined,
        streaming: false,
      }) as const,
    [activeAnalyses],
  );

  const overview = useQuery("/projects/:projectId(uuid)/all-analyses", {
    routeParams: { projectId },
    query: {
      from: overviewSampling!.from,
      to: overviewSampling!.to,
      intervalSeconds: overviewSampling!.intervalSeconds,
      sourceId: source.id,
      quantityIds: quantityIds.join(","),
      mustHaveLastAnalysis: true, // important for UX related to Highcharts' navigator
    },
    disabled: overviewSampling === null || !doIt,
    placeholderData,
    keepPreviousData: true,
  })?.body;

  const windowQuery = React.useMemo(
    () =>
      getWindowSamplingQuery({
        quantityIds,
        period,
        source,
      }),
    [quantityIds, period, source],
  );

  const window = useQuery("/projects/:projectId(uuid)/all-analyses", {
    routeParams: { projectId },
    query: windowQuery,
    disabled: windowSampling === null || !doIt,
    placeholderData,
    keepPreviousData: true,
  })?.body;

  const description = React.useMemo(() => {
    if (!window || !overview || !windowSampling) return null;
    const samplingLevel = Math.floor((10000 * window.result.length) / window.unsampledCount) / 100;
    const duration =
      windowSampling.intervalSeconds === 0
        ? null
        : Duration.fromMillis(windowSampling.intervalSeconds * 1000, { locale });

    return duration === null
      ? null
      : t("millions.sampling.description", {
          humanDuration: toHuman(duration),
          samplingLevel,
        });
  }, [overview, window, windowSampling]);

  const analyses = React.useMemo(
    () =>
      !window || !overview
        ? []
        : arrayUniqueByKey([...overview.result, ...window.result, ...activeAnalyses], "id").sort(
            comparatorBy("date"),
          ),
    [overview, window, activeAnalyses],
  );

  return {
    result: analyses,
    description,
  };
}

export type AnalysisQuantityValue = Analysis["quantityValues"][number];

export default useAllAnalyses;
